import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import FocusEditor from '../Editor/FocusEditor';
import Toolbar from '../Editor/Toolbar';
import StatusBar from '../Editor/StatusBar';
import { useSettingsStore } from '../../stores/settingsStore';
import { useEditorStore } from '../../stores/editorStore';
import { useHighContrastMode, useReducedMotion } from '../../hooks/useAccessibility';
import { useBackgroundImage, preloadBackgroundImages } from '../../hooks/useBackgroundImage';

const AppLayout: React.FC = () => {
  const { theme, showToolbar, backgroundStyle, backgroundColor, backgroundImage } = useSettingsStore();
  const { isFullscreen } = useEditorStore();
  // 메뉴바 항상 표시 (자동 숨김 기능 제거)
  const isMouseMoving = true;
  
  // 접근성 향상
  useHighContrastMode();
  useReducedMotion();

  // 🎨 배경 이미지 로딩 (fallback 처리 포함)
  const fallbackGradient = backgroundColor.includes('linear-gradient') 
    ? backgroundColor 
    : `linear-gradient(135deg, ${backgroundColor} 0%, ${backgroundColor}88 100%)`;
  
  const { imageUrl: loadedImageUrl, isLoading, hasError } = useBackgroundImage(
    backgroundImage?.url || null, 
    fallbackGradient
  );

  // 배경 이미지들 프리로딩 (성능 향상)
  useEffect(() => {
    const imageUrls = [
      '/bg-images/ash-edmonds-uztw2giebSc-unsplash.jpg',
      '/bg-images/jezael-melgoza-7H77FWkK_x4-unsplash.jpg',
      '/bg-images/sora-sagano-8sOZJ8JF0S8-unsplash.jpg',
      '/bg-images/su-san-lee-E_eWwM29wfU-unsplash.jpg',
      '/bg-images/vista-wei-OiERUvVrioU-unsplash.jpg',
    ];
    preloadBackgroundImages(imageUrls);
  }, []);

  useEffect(() => {
    // 테마 적용
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);


  const getBackgroundStyle = () => {
    if (backgroundStyle === 'image' && backgroundImage) {
      // 🖼️ 루트 images/ 폴더의 실제 이미지 사용 (fallback 포함)
      if (hasError) {
        // 이미지 로딩 실패시 fallback gradient 사용
        return {
          background: fallbackGradient,
        };
      }
      
      return {
        backgroundImage: `url(${loadedImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        filter: backgroundImage.blur > 0 ? `blur(${backgroundImage.blur}px)` : 'none',
      };
    }
    
    if (backgroundStyle === 'gradient') {
      return {
        background: backgroundColor.includes('linear-gradient') 
          ? backgroundColor 
          : `linear-gradient(135deg, ${backgroundColor} 0%, ${backgroundColor}88 100%)`,
      };
    }
    
    return {
      backgroundColor,
    };
  };

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden"
      style={getBackgroundStyle()}
    >
      {backgroundStyle === 'image' && backgroundImage && (
        <div 
          className="absolute inset-0"
          style={{
            backgroundColor: `rgba(0, 0, 0, ${backgroundImage.overlay})`,
          }}
        />
      )}
      
      {isLoading && backgroundStyle === 'image' && (
        <div className="absolute top-4 right-4 z-50">
          <div className="flex items-center space-x-2 px-3 py-2 glass-effect rounded-lg">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            <span className="text-sm text-gray-700 dark:text-gray-300">Loading background...</span>
          </div>
        </div>
      )}
      
      <motion.div
        animate={{ opacity: isFullscreen ? 0 : 1 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/5 dark:to-black/20"
      />
      
      {showToolbar && isMouseMoving && (
        <Toolbar />
      )}
      
      <div className={`${isFullscreen ? 'pt-0' : 'pt-16'} pb-16 transition-all duration-300`}>
        <FocusEditor />
      </div>
      
      {isMouseMoving && (
        <StatusBar />
      )}
    </div>
  );
};

export default AppLayout;